import React, {FunctionComponent, useEffect, useState, useRef} from 'react';
import {
  Intent,
  Position,
  Toaster,
  MultistepDialog,
  DialogStep,
  Classes,
} from '@blueprintjs/core';

import {IOrganization, IUser} from '../../../../../shared/models';
import CompleteProfileStep from './CompleteProfileStep';
import {isAdmin} from '../../../../../shared/utils/auth';
import {nameMatch} from '../../../../../shared/utils/text';

import './OnboardingDialog.scss';

const completeProfileToaster = Toaster.create({
  position: Position.TOP,
  usePortal: true,
  className: 'complete-profile-toaster',
});

type Callback = (value?: any) => void;

interface ICompleteProfileComponentProps {
  organization: IOrganization | null;
  currentUser: IUser | null | undefined;
  onSubmit: Callback;
  saved: boolean;
  show: boolean;
  onClose: any;
}

export interface IOnboardingFormState {
  firstName: any;
  lastName: any;
  companyName: any;
}

export interface IOnboardingFormStateErrors {
  firstName: boolean;
  lastName: boolean;
  companyName: boolean;
}

const OnRender = ({ onRender, children }: any) => {
  useEffect(() => {
    onRender();
  }, []);

  return children;
};

const CSS_CLASS_NAME = 'onboarding-dialog';

const OnboardingDialog: FunctionComponent<ICompleteProfileComponentProps> = ({
  organization,
  currentUser,
  onSubmit,
  saved,
  show,
  onClose,
}: ICompleteProfileComponentProps) => {
  const initialFormStateErrors = {
    firstName: true,
    lastName: true,
    companyName: true,
  };
  const [formState, setFormState] = useState<IOnboardingFormState | null>(null);
  const [formStateErrors, setFormStateErrors] = useState(
    initialFormStateErrors
  );
  const [isFormValid, setIsFormValid] = useState(true);
  const [dialogStepIndex, setDialogStepIndex] = useState<number>(0);
  const dialogRef = useRef<any>(null);

  const goToNextStep = () => {
    /*
     * This is what happens in BlueprintJS MultistepDialog
     * https://github.com/palantir/blueprint/blob/develop/packages/core/src/components/dialog/multistepDialog.tsx#L264
     */
    const currentIndex = dialogRef.current.state.selectedIndex;
    const nextIndex = currentIndex + 1;

    dialogRef.current?.setState({
      // we need to keep lastViewedIndex the highest value because of how `hasBeenViewed` is calculated
      // const hasBeenViewed = this.state.lastViewedIndex >= index;
      lastViewedIndex: Math.max(
        dialogRef.current.state.lastViewedIndex,
        nextIndex
      ),
      selectedIndex: nextIndex,
    });
    setDialogStepIndex(nextIndex);
    disablePrevSteps();
  };

  const disablePrevSteps = () => {
    const previousSteps = document
      .querySelector(`.${CSS_CLASS_NAME}`)
      ?.querySelectorAll(`.${Classes.DIALOG_STEP_VIEWED}`);
    // :not(.${Classes.ACTIVE})

    previousSteps?.forEach((element: any, index: number) => {
      if (index < dialogStepIndex) {
        element.classList.remove(Classes.DIALOG_STEP_VIEWED);
        const clone = element.cloneNode(true);
        element.parentNode.replaceChild(clone, element);
      }
    });
  };

  useEffect(() => {
    if (saved) {
      completeProfileToaster.show({
        message: 'Profile updated successfully!',
        intent: Intent.SUCCESS,
      });
    }
  }, [saved]);

  useEffect(() => {
    if (formState === null && organization && currentUser) {
      setFormState({
        firstName: currentUser.first_name || '',
        lastName: currentUser.last_name || '',
        companyName: organization.name || '',
      });
    }
  }, [organization, currentUser, formState]);

  useEffect(() => {
    if (!formState) {
      return;
    }

    setIsFormValid(
      isAdmin(currentUser)
        ? Object.values(formStateErrors).every((entry) => entry) &&
            Object.values(formState).every((value) => value && value.length > 1)
        : Object.values(formStateErrors).every((entry) => entry) &&
            Object.values([
              formState.firstName,
              formState.lastName,
            ]).every((value) => value && value.length > 1)
    );
  }, [formStateErrors, formState]);

  const onChangeInput = (key: string, value: string) => {
    switch (key) {
      default:
        setFormState((prev: any) => {
          const newState = { ...prev };
          newState[key] = value;
          return newState;
        });
        setFormStateErrors((prev: any) => {
          const newState = { ...prev };
          newState[key] = nameMatch(value);
          return newState;
        });
    }
  };

  const onClickSubmit = () => {
    onSubmit(formState);
    // goToNextStep();
    onClose();
  };

  if (!organization || !currentUser) {
    return null;
  }

  return (
    <MultistepDialog
      isOpen={show}
      usePortal={true}
      ref={dialogRef}
      navigationPosition="top"
      finalButtonProps={{
        text: 'Submit',
        disabled: !isFormValid,
        onClick: onClickSubmit,
      }}
      className={CSS_CLASS_NAME}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
    >
      <DialogStep
        title="Complete your Profile"
        id="STEP_2"
        className="complete-profile"
        backButtonProps={{ className: 'hide-button' }}
        panel={
          <OnRender onRender={disablePrevSteps}>
            <CompleteProfileStep
              currentUser={currentUser}
              formState={formState}
              formStateErrors={formStateErrors}
              onChangeInput={onChangeInput}
            />
          </OnRender>
        }
      />
    </MultistepDialog>
  );
};

export default OnboardingDialog;
